import type { AuthError, Session } from '@supabase/supabase-js'
import { createStore, useStore } from 'zustand'

import { AuthService } from '@/modules/auth'
import {
  InvalidCredentialsError,
  OtpRateLimitError,
  SignInAccountNotFoundError,
  SignInError,
} from '@/modules/errors'
import { eventTracker } from '@/services/tracking'
import { OTP_RESEND_COOLDOWN_SECONDS } from '../constants'

const TOO_MANY_REQUESTS = 429

// Supabase only says how long to wait in prose: "you can only request this after 42 seconds".
const secondsToWait = (message: string) =>
  Number(message.match(/(\d+) seconds?/)?.[1] ?? OTP_RESEND_COOLDOWN_SECONDS)

const toOtpRequestError = (error: AuthError) =>
  error.status === TOO_MANY_REQUESTS
    ? new OtpRateLimitError(secondsToWait(error.message))
    : new SignInError(error.message)

type AuthStoreActions = {
  clearSession: () => void
}
type AuthStoreMutations = {
  signIn: ({ email }: { email: string }) => Promise<void>
  signUp: ({
    email,
    metadata,
  }: {
    email: string
    metadata?: object
  }) => Promise<void>
  signOut: () => Promise<void>
  verifyOtp: ({ code, email }: { code: string; email: string }) => Promise<void>
}
type AuthStoreLoaders = {
  getSession: () => Promise<Session | null>
}

export interface AuthStore {
  session: Session | null
  user: AuthService.User | null
  actions: AuthStoreActions
  mutations: AuthStoreMutations
  mutating: boolean
  loaders: AuthStoreLoaders
}

const authStore = createStore<AuthStore>()((set, get) => ({
  session: null,
  user: null,
  mutating: false,
  actions: {
    clearSession: () => set({ session: null, user: null }),
  },
  mutations: {
    signIn: async ({ email }) => {
      const { error } = await AuthService.signIn({ email })
      if (error) {
        if (
          error?.code === 'otp_disabled' &&
          error.message.includes('Signups not allowed for otp')
        ) {
          throw new SignInAccountNotFoundError()
        }

        console.error(error)
        throw toOtpRequestError(error)
      }
    },

    signUp: async ({ email, metadata }) => {
      set({ mutating: true })
      const { error } = await AuthService.signUp({ email, metadata }).finally(
        () => set({ mutating: false }),
      )
      if (error) throw toOtpRequestError(error)
    },

    verifyOtp: async ({ code, email }) => {
      const { data, error } = await AuthService.verifyOtp({ code, email })
      if (error || !data.session) {
        set({ session: null })
        throw new InvalidCredentialsError()
      }

      const { confirmed_at, created_at, user_metadata } = data.session.user

      eventTracker.signedIn({
        email,
        confirmed_at,
        created_at,
        ...user_metadata,
      })

      const { user } = await AuthService.getUser()
      set({ session: data.session, user: user || null })
    },

    signOut: async () => {
      await AuthService.signOut()
      set({ session: null })
    },
  },

  loaders: {
    getSession: async () => {
      const session = get().session
      if (session) return session

      const { data } = await AuthService.getSession()
      // Without a session, getUser signs out and emits SIGNED_OUT again.
      if (!data.session) return null

      const { user } = await AuthService.getUser()
      set({ session: data.session, user: user || null })
      return data.session
    },
  },
}))

const useAuthStore = <T>(selector: (state: AuthStore) => T): T =>
  useStore(authStore, selector)

export const authLoaders = () => authStore.getState().loaders

export const useUser = () => useAuthStore((state) => state.user)
export const useAuthActions = () => useAuthStore((state) => state.actions)
export const useAuthMutations = () => useAuthStore((state) => state.mutations)
export const useAuthLoaders = () => useAuthStore((state) => state.loaders)
export const useAuthMutating = () => useAuthStore((state) => state.mutating)
